import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box } from '@rsos/base-components';
import { CALL_OUT_OF_JURISDICTION } from '@rsos/constants/collaborator';
import useTF911LoggedInUser from '@rsos/hooks/talkjs/useTF911LoggedInUser';
import { useGetSelectedCollaborator } from '@rsos/reduxDataStore/src/features/emergencyCalls/helpers';
import { CHAT_TAB } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneTabs';
import { setTempMessage } from '@rsos/reduxDataStore/src/features/outboundSms/outboundSmsSlice';
import {
  useSelectedTextFrom911ConversationID,
  useSelectedTextFrom911Conversation,
} from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Selectors';
import {
  TF911_CONVERSATION_ENDED,
  setConversationUnread,
  setPendingTF911Message,
} from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Slice';
import { createTalkSession, createTalkUser } from '@rsos/utils/talkJS';
import useHasAccessToUniteEventPane from '../../../../hooks/useHasAccessToUniteEventPane';
import useTalkJSTheme from '../../../../hooks/useTalkJSTheme';
import { PRECONFIG_REQUEST_LOCATION } from '../../../PreconfiguredMessages/AllMessages/utils';
import SuggestedResponses from '../../../PreconfiguredMessages/SuggestedResponses';
import RequestLocation from '../../../RequestLocation';
import { ChatContainer } from './TF911.styles';
import {
  trackSendPreconfiguredResponse,
  trackViewAllPreconfiguredResponses,
} from './TF911ChatBoxTrackings';

/**
 * Create a TalkJS chat session for a given caller data
 * @param {object} props - The props object for the component
 * @param {string} props.callerID - The caller phone number
 * @param {boolean} props.showPreconfiguredMessages - Determines if preconfigured messages should be displayed
 * @param {boolean} props.showStartChat - Controls the height and max height of the component
 * @param {boolean} props.isTextFrom911ChatBoxVisible - Controls the overflowY styling of the chat box
 * @param {function} props.setIsAllMessagesVisible - Callback to set the visibility of all messages
 * @param {object} props.selectedPreconfiguredMessage - The selected preconfigured message object
 * @param {function} props.setSelectedPreconfiguredMessage - Callback to set the selected preconfigured message
 */
const TF911Chat = ({
  callerID,
  isTextFrom911ChatBoxVisible,
  preconfiguredMessages,
  setIsAllMessagesVisible,
  selectedPreconfiguredMessage,
  setSelectedPreconfiguredMessage,
  hasRequestLocationAccess,
}) => {
  const dispatch = useDispatch();
  const tempMessage = useSelector(state => state.outboundSms.tempMessage);
  const selectedTab = useSelector(state => state.eventPane.selectedTab);
  const isTextFrom911ChatVisibleRedux = useSelector(
    state => state.eventPane.chatVisibility?.isTextFrom911ChatVisible,
  );

  const collaborator = useGetSelectedCollaborator();
  const isOutOfJurisdiction = collaborator.type === CALL_OUT_OF_JURISDICTION;

  const chatRef = useRef();

  const [chatSession, setChatSession] = useState(null);
  const [chatbox, setChatbox] = useState(null);

  const chatTheme = useTalkJSTheme(true);

  const conversationID = useSelectedTextFrom911ConversationID(callerID);

  const conversation = useSelectedTextFrom911Conversation(callerID);
  const hasUnreadMessages = conversation?.ui?.hasUnreadMessages;

  const hasAccessToUniteEventPane = useHasAccessToUniteEventPane();

  const isConversationEnded = useMemo(
    () => conversation?.data?.custom?.STATUS === TF911_CONVERSATION_ENDED,
    [conversation],
  );
  const pendingTF911Message = useSelector(
    state => state.textFrom911.pendingTF911Message,
  );
  const selectedConversation = useSelector(
    state => state.textFrom911.selectedConversation,
  );

  const shouldShowChatButtons =
    !isConversationEnded &&
    isTextFrom911ChatBoxVisible &&
    conversationID &&
    !isOutOfJurisdiction;

  const hasSuggestedResponses =
    hasAccessToUniteEventPane && shouldShowChatButtons;

  const loggedInUser = useTF911LoggedInUser();

  const messageFieldVisibility = useMemo(() => {
    if (isOutOfJurisdiction) {
      return false;
    }

    return !isConversationEnded;
  }, [isConversationEnded, isOutOfJurisdiction]);

  // Mount the TalkJS session and chat container
  useEffect(() => {
    let session, chat;
    try {
      (async () => {
        session = await createTalkSession(loggedInUser);

        chat = session.createChatbox({
          theme: chatTheme,
          showChatHeader: false,
          messageField: {
            visible: messageFieldVisibility,
            placeholder: 'Type Message',
          },
        });

        chat.mount(chatRef.current);

        setChatSession(session);

        setChatbox(chat);
      })();
    } catch (error) {
      if (session) {
        setChatbox(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatTheme]);

  // Set Conversation
  useEffect(() => {
    if (chatbox && chatSession && conversationID) {
      const conversation = chatSession.getOrCreateConversation(conversationID);
      if (pendingTF911Message && selectedConversation) {
        conversation.sendMessage(pendingTF911Message);
        dispatch(setPendingTF911Message(null));
      }
      // TF911 Conversation Setup

      conversation.setParticipant(createTalkUser(loggedInUser));
      chatbox.select(conversation);

      chatbox.messageField.setVisible(messageFieldVisibility);

      chatSession.onMessage(message => {
        // Handle preconfigured messages tracking
        const matchedMessage = preconfiguredMessages?.find(
          msg => msg.body === message.body,
        );

        if (matchedMessage) {
          trackSendPreconfiguredResponse(matchedMessage, conversationID);
        }
      });
    }
  }, [
    chatSession,
    chatbox,
    conversationID,
    isConversationEnded,
    loggedInUser,
    preconfiguredMessages,
    pendingTF911Message,
    selectedConversation,
  ]);

  useEffect(() => {
    // use redux state for visibility, but fall back to prop for backward compatibility
    const chatVisible =
      isTextFrom911ChatVisibleRedux !== undefined
        ? isTextFrom911ChatVisibleRedux
        : isTextFrom911ChatBoxVisible;
    if (
      selectedTab === CHAT_TAB &&
      chatVisible &&
      conversationID &&
      chatSession &&
      chatbox &&
      isTextFrom911ChatBoxVisible &&
      hasUnreadMessages
    ) {
      dispatch(setConversationUnread({ callerID, hasUnreadMessages: false }));
    }
  }, [
    selectedTab,
    isTextFrom911ChatVisibleRedux,
    conversationID,
    chatSession,
    chatbox,
    isTextFrom911ChatBoxVisible,
    hasUnreadMessages,
    callerID,
    dispatch,
  ]);

  // Set messageField text
  useEffect(() => {
    if (chatbox && !isConversationEnded) {
      if (selectedPreconfiguredMessage) {
        chatbox.messageField.setText(selectedPreconfiguredMessage.body);
        setSelectedPreconfiguredMessage(null);
      }
      if (tempMessage) {
        chatbox.messageField.setText(tempMessage);
        dispatch(setTempMessage(null));
      }
    }
  }, [
    selectedPreconfiguredMessage,
    chatbox,
    isConversationEnded,
    tempMessage,
    setSelectedPreconfiguredMessage,
    dispatch,
  ]);

  const handleViewClick = () => {
    trackViewAllPreconfiguredResponses(conversationID);
    setIsAllMessagesVisible(true);
  };

  const handleRequestLocation = url => {
    if (chatbox && !isConversationEnded && preconfiguredMessages) {
      const locationMessage = preconfiguredMessages.find(
        message => message.title === PRECONFIG_REQUEST_LOCATION,
      );

      const talkjsMessage = locationMessage.body.replace(/\{\{.*?\}\}/g, url);
      chatbox.messageField.setText(talkjsMessage);
    }
  };

  return (
    <ChatContainer
      height="100%"
      display={isTextFrom911ChatBoxVisible && conversationID ? 'flex' : 'none'}
      showBorder={isTextFrom911ChatBoxVisible && conversationID}
      border="1px solid"
      borderRadius="0 5px 5px 0"
      borderColor="lightLineDivider"
    >
      <Box ref={chatRef} height="100%" />
      {hasSuggestedResponses && (
        <Box width="100%" horizontal align="center">
          {hasRequestLocationAccess && (
            <RequestLocation onSuccess={handleRequestLocation} />
          )}
          <SuggestedResponses handleViewClick={handleViewClick} />
        </Box>
      )}
    </ChatContainer>
  );
};
export default TF911Chat;
